import { useState, useEffect, useCallback, useMemo } from 'react';
import { projectsApi, ProjectWithBuilding } from '../../api/projectsApi';
import { ProjectStatus } from '../../types/projects';

interface UseProjectsReturn {
    projects: ProjectWithBuilding[];
    planned: ProjectWithBuilding[];
    active: ProjectWithBuilding[];
    completed: ProjectWithBuilding[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

/**
 * Hook to fetch and manage all projects.
 * Provides computed properties for planned, active and completed projects.
 *
 * @param completedYear - Optional year to filter completed projects by actualEndDate
 * @param buildingKey - Optional building to restrict every list to
 */
export function useProjects(completedYear?: number, buildingKey?: string): UseProjectsReturn {
    const [allProjects, setAllProjects] = useState<ProjectWithBuilding[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadProjects = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const loaded = await projectsApi.getAllProjects();
            setAllProjects(loaded);
        } catch (err) {
            console.error('Error loading projects:', err);
            setError('Kunde inte ladda projekt');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    const projects = useMemo(() =>
        buildingKey ? allProjects.filter(p => p.buildingKey === buildingKey) : allProjects,
        [allProjects, buildingKey]
    );

    const planned = useMemo(() =>
        projects.filter(p => p.status === ProjectStatus.PLANNED),
        [projects]
    );

    const active = useMemo(() =>
        projects.filter(p => p.status === ProjectStatus.IN_PROGRESS),
        [projects]
    );

    const completed = useMemo(() =>
        projects.filter(p =>
            p.status === ProjectStatus.COMPLETED &&
            (!completedYear || p.actualEndDate?.startsWith(String(completedYear)))
        ),
        [projects, completedYear]
    );

    return { projects, planned, active, completed, loading, error, refresh: loadProjects };
}
